import CopyAllIcon from '@mui/icons-material/CopyAll';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Link, Tooltip } from '@mui/material';
import { isNil } from 'lodash-es';
import { FC, useCallback, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Table } from 'react-bootstrap';
import { useAsync, useMount, useSearchParam } from 'react-use';
import { Permissions } from 'RtExports/routes';
import {
	AccountIndexResponse,
	CallProcessingResult,
	GatewayIndexResponse,
	RateCenterIndexResponse,
	SipCdrSearchIndexResponse,
	SipCdrSearchProfileResponse,
	SipConnection,
	SubscriptionIndexResponse
} from 'RtModels';
import { AccountRouter } from 'RtUi/app/AccountManagement/Accounts/Account.router';
import { ReleaseCodeResource } from 'RtUi/app/AccountManagement/Subscriptions/lib/resources/ReleaseCodeResource';
import { SubscriptionRouter } from 'RtUi/app/AccountManagement/Subscriptions/Subscription.router';
import {
	ApplicationNotifications,
	ApplicationNotificationTypes,
	INotification
} from 'RtUi/app/ApplicationShell/lib/components/ApplicationNotifications';
import { ServerRouter } from 'RtUi/app/rtAdmin/Servers/Server.router';
import { CdrSearchReportRouter } from 'RtUi/app/rtSip/CdrSearchReport/CdrSearchReport.router';
import {
	useGetCallProcessingResults,
	useGetCdrSearch
} from 'RtUi/app/rtSip/CdrSearchReport/lib/services';
import { ConnectionRouter } from 'RtUi/app/rtSip/Connections/Connection.router';
import { RtxJurisdiction } from 'RtUi/app/rtSip/rtSipConstants';
import { SipTraceResource } from 'RtUi/app/rtSip/SipTrace/lib/resource/SipTraceResource';
import { SipTraceView } from 'RtUi/app/rtSip/SipTrace/lib/SipTraceView';
import { useIdParam } from 'RtUi/components/hooks/useIdParam';
import { RtxAccountSelect } from 'RtUi/components/rtx/inputs/Select/instances/Account/RtxAccountSelect';
import { RtxGatewaySelect } from 'RtUi/components/rtx/inputs/Select/instances/Gateway/RtxGatewaySelect';
import { RtxRateCenterSelect } from 'RtUi/components/rtx/inputs/Select/instances/RateCenter/RtxRateCenterSelect';
import { RtxSipConnectionSelect } from 'RtUi/components/rtx/inputs/Select/instances/SipConnection/RtxSipConnectionSelect';
import { RtxSubscriptionSelect } from 'RtUi/components/rtx/inputs/Select/instances/Subscription/RtxSubscriptionSelect';
import { RtxTextInput } from 'RtUi/components/rtx/inputs/Text/RtxTextInput';
import { Loading } from 'RtUi/components/ui/Loading';
import { RtUiForm } from 'RtUi/components/ui/RtUiForm';
import { TabbedLayout, TabbedLayoutTab } from 'RtUi/components/ui/TabbedLayout';
import { useTabs } from 'RtUi/components/ui/TabbedLayout/useTabs';
import { UiNotification } from 'RtUi/notifications/lib/UiNotification';
import { UserActions } from 'RtUi/state/actions/user';
import { timestampToReadable } from 'RtUi/utils/maps';

export const CdrSearchReportProfileContainer: FC<
	React.PropsWithChildren<unknown>
> = () => {
	const id = useIdParam();
	const tabId = useSearchParam('tabId');

	const { data: cdrProfile, isLoading } = useGetCdrSearch(id);
	const { data: CallProcessingResultsData } = useGetCallProcessingResults();

	const sipTraceResource = useMemo(
		() =>
			cdrProfile &&
			new SipTraceResource({
				startTime: cdrProfile.iInviteTs,
				endTime: cdrProfile.iReleaseTs,
				gatewayId: cdrProfile.iGatewayId
			}),
		[cdrProfile]
	);

	const [tabs, activeTab, setActiveTab] = useTabs(
		CdrSearchReportRouter.getProfileTabs(cdrProfile)
	);

	useMount(() => {
		if (tabId && activeTab.header !== tabId) {
			setActiveTab(tabId);
		}
	});

	const [iRateCenter, setIRateCenter] = useState<RateCenterIndexResponse>();
	const [eRateCenter, setERateCenter] = useState<RateCenterIndexResponse>();
	const [iVendorAccount, setIVendorAccount] = useState<AccountIndexResponse>();
	const [iVendorSubscription, setIVendorSubscription] =
		useState<SubscriptionIndexResponse>();
	const [eVendorAccount, setEVendorAccount] = useState<AccountIndexResponse>();
	const [eVendorSubscription, setEVendorSubscription] =
		useState<SubscriptionIndexResponse>();
	const [iCustAccount, setICustAccount] = useState<AccountIndexResponse>();
	const [iCustSubscription, setICustSubscription] =
		useState<SubscriptionIndexResponse>();
	const [eCustAccount, setECustAccount] = useState<AccountIndexResponse>();
	const [eCustSubscription, setECustSubscription] =
		useState<SubscriptionIndexResponse>();
	const [iGateway, setIGateway] = useState<GatewayIndexResponse>();
	const [eGateway, setEGateway] = useState<GatewayIndexResponse>();
	const [iPeerId, setIPeerId] = useState<SipConnection>();
	const [ePeerId, setEPeerId] = useState<SipConnection>();

	const releaseCodes = useAsync(async () => {
		const rs = new ReleaseCodeResource();
		return rs.getAll();
	});

	const jurisdictionLabel = (jurisdictionId: number) => {
		let ret = '';
		if (RtxJurisdiction[jurisdictionId as keyof typeof RtxJurisdiction]) {
			ret = `${RtxJurisdiction[jurisdictionId as keyof typeof RtxJurisdiction]} (${jurisdictionId})`;
		}
		return ret;
	};

	const getReleaseCodeReadable = useCallback(
		(releaseCodeId: number) => {
			if (releaseCodes.loading) {
				return releaseCodeId;
			}

			const rCode = releaseCodes.value?.find(
				(rs) => rs.releaseCodeId === releaseCodeId
			);

			if (rCode?.label) {
				return `${rCode.label} (${releaseCodeId})`;
			}

			return releaseCodeId;
		},
		[releaseCodes]
	);

	if (isLoading) {
		return <Loading />;
	}

	if (!cdrProfile) {
		return <p>Not Found</p>;
	}

	const getCallProcessingResult = (result?: CallProcessingResult): string => {
		if (!isNil(result) && CallProcessingResultsData) {
			const data = CallProcessingResultsData.find(
				(res) => res.resultId === Number(result)
			);

			return data?.summary ? `${data.summary} (${result})` : '';
		}

		return String(result ?? '');
	};

	const copyToClipboard = () => {
		const cdrData = {
			'CDR ID': cdrProfile.cdrId,

			'Calling Party Source': cdrProfile.cp,
			'Calling Party Lrn': cdrProfile.cpLrn,
			'Calling Party Std': cdrProfile.cpStd,
			'Calling Party RateCenter': iRateCenter?.label,

			'Dialed Number Source': cdrProfile.dn,
			'Dialed Number Lrn': cdrProfile.dnLrn,
			'Dialed Number Std': cdrProfile.dnStd,
			'Dialed Party RateCenter': eRateCenter?.label || '',

			'Ingress Local Gateway': iGateway
				? `${iGateway?.listenFqdn}:${iGateway.listenPort}`
				: '',
			'Ingress Remote Gateway': iPeerId
				? `${iPeerId?.hostAddress}:${iPeerId.hostPort}`
				: '',
			'Ingress Invite Timestamp': cdrProfile.iInviteTs
				? timestampToReadable(
						cdrProfile.iInviteTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Ingress Answer Timestamp': cdrProfile.iAnswerTs
				? timestampToReadable(
						cdrProfile.iAnswerTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Ingress Release Timestamp': cdrProfile.iReleaseTs
				? timestampToReadable(
						cdrProfile.iReleaseTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Ingress Duration (secs)': cdrProfile.iDuration
				? `${cdrProfile.iDuration}`
				: '',
			'Ingress Result': cdrProfile.iResult
				? `${getCallProcessingResult(cdrProfile.iResult)}`
				: '',
			'Ingress Call ID': cdrProfile.iCallId,

			'Egress Local Gateway': eGateway
				? `${eGateway?.listenFqdn}:${eGateway.listenPort}`
				: '',
			'Egress Remote Gateway': ePeerId
				? `${ePeerId?.hostAddress}:${ePeerId.hostPort}`
				: '',
			'Egress Invite Timestamp': cdrProfile.eInviteTs
				? timestampToReadable(
						cdrProfile.eInviteTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Egress Answer Timestamp': cdrProfile.eAnswerTs
				? timestampToReadable(
						cdrProfile.eAnswerTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Egress Release Timestamp': cdrProfile.eReleaseTs
				? timestampToReadable(
						cdrProfile.eReleaseTs,
						'UTC',
						true,
						'YYYY-MM-DD HH:mm:ss.SSS'
					)
				: '',
			'Egress Duration (secs)': cdrProfile.eDuration
				? `${cdrProfile.eDuration}`
				: '',
			'Egress Result': cdrProfile.eResult
				? `${getCallProcessingResult(cdrProfile.eResult)}`
				: '',
			'Egress Call ID': cdrProfile.eCallId,

			'Ingress Customer Rating Account': iVendorAccount?.companyName || '',
			'Ingress Customer Rating Subscription': iVendorSubscription?.label || '',
			'Ingress Customer Rating Jurisdiction': jurisdictionLabel(
				cdrProfile.iRevenueJurisdictionId
			),
			'Ingress Customer Rating Duration (secs)': cdrProfile.iRevenueDuration
				? `${cdrProfile.iRevenueDuration}`
				: '',
			'Ingress Customer Rating Rate Key': cdrProfile.iRevenueRateKey || '',
			'Ingress Customer Rating Rate': cdrProfile.iRevenueRate
				? `${cdrProfile.iRevenueRate}`
				: '',
			'Ingress Customer Rating Charges': cdrProfile.iRevenueRateCharge
				? `${cdrProfile.iRevenueRateCharge}`
				: '',

			'Ingress Vendor Rating Account': iCustAccount?.companyName || '',
			'Ingress Vendor Rating Subscription': iCustSubscription?.label || '',
			'Ingress Vendor Rating Jurisdiction': jurisdictionLabel(
				cdrProfile.iCostJurisdictionId
			),
			'Ingress Vendor Rating Duration (secs)': cdrProfile.iCostDuration
				? `${cdrProfile.iCostDuration}`
				: '',
			'Ingress Vendor Rating Rate Key': cdrProfile.iCostRateKey || '',
			'Ingress Vendor Rating Rate': cdrProfile.iCostRate
				? `${cdrProfile.iCostRate}`
				: '',
			'Ingress Vendor Rating Charges': cdrProfile.iCostRateCharge
				? `${cdrProfile.iCostRateCharge}`
				: '',

			'Egress Customer Rating Account': eVendorAccount?.companyName || '',
			'Egress Customer Rating Subscription': eVendorSubscription?.label || '',
			'Egress Customer Rating Jurisdiction': jurisdictionLabel(
				cdrProfile.eRevenueJurisdictionId
			),
			'Egress Customer Rating Duration (secs)': cdrProfile.eRevenueDuration
				? `${cdrProfile.eRevenueDuration}`
				: '',
			'Egress Customer Rating Rate Key': cdrProfile.eRevenueRateKey || '',
			'Egress Customer Rating Rate': cdrProfile.eRevenueRate
				? `${cdrProfile.eRevenueRate}`
				: '',
			'Egress Customer Rating Charges': cdrProfile.eRevenueRateCharge
				? `${cdrProfile.eRevenueRateCharge}`
				: '',

			'Egress Vendor Rating Account': eCustAccount?.companyName || '',
			'Egress Vendor Rating Subscription': eCustSubscription?.label || '',
			'Egress Vendor Rating Jurisdiction': jurisdictionLabel(
				cdrProfile.eCostJurisdictionId
			),
			'Egress Vendor Rating Duration (secs)': cdrProfile.eCostDuration
				? `${cdrProfile.eCostDuration}`
				: '',
			'Egress Vendor Rating Rate Key': cdrProfile.eCostRateKey || '',
			'Egress Vendor Rating Rate': cdrProfile.eCostRate
				? `${cdrProfile.eCostRate}`
				: '',
			'Egress Vendor Rating Charges': cdrProfile.eCostRateCharge
				? `${cdrProfile.eCostRateCharge}`
				: ''
		};

		let cdrDataString = objToText(cdrData);

		cdrDataString += '\n';
		cdrProfile.routeAttempts.forEach((attempt, index) => {
			cdrDataString += `\nRoute Attempt ${index + 1}:\n`;
			cdrDataString += objToText({
				'Route Attempt ID': attempt.aEgrRouteDepth,
				'Route Attempt Timestamp': timestampToReadable(
					attempt.aEgrInvTs,
					'UTC',
					true,
					'YYYY-MM-DD HH:mm:ss'
				),
				'Route Attempt Subscription': attempt.aEgrCostSubsId,
				'Route Attempt Vendor GW': attempt.egrSummary,
				'Route Attempt Tech Prefix': attempt.egrTechPrefix,
				'Route Attempt PDD': attempt.aPostDialDelay,
				'Route Attempt Dialed Number': attempt.aEgrNumber,
				'Route Attempt Release': getReleaseCodeReadable(attempt.aEgrReleaseId)
			});
			cdrDataString += '\n';
		});

		navigator.clipboard.writeText(cdrDataString);

		const notification: INotification = {
			message: 'CDR Copied to clipboard',
			title: 'Copy To Clipboard',
			notificationId: `Cdr-Export-To-Clipboard-${Math.random()}`,
			notificationTypeId: ApplicationNotificationTypes.Success,
			effectiveTs: new Date(),
			expirationTs: new Date(),
			isUrgent: 0
		};

		ApplicationNotifications.openNotification(notification, {
			autoClose: 2_000
		});
	};

	const objToText = (obj: Record<string, string | number | undefined>) => {
		let maxLength = 0;
		Object.keys(obj).forEach((key) => {
			if (key.length > maxLength) {
				maxLength = key.length;
			}
		});

		return Object.entries(obj)
			.map(([key, value]) => `${key.padEnd(maxLength)}: ${value}`)
			.join('\n');
	};

	return (
		<TabbedLayout<SipCdrSearchProfileResponse>
			router={CdrSearchReportRouter}
			profile={cdrProfile}
			activeTab={activeTab.header}
			onTabChange={(activeTab) => {
				setActiveTab(activeTab);
			}}
		>
			<TabbedLayoutTab {...tabs.Profile}>
				<RtUiForm
					hideButtons
					createMode={false}
					displayMode={true}
					isInline
					className="mb-3"
				>
					<Row>
						<Col lg={12}>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'flex-end',
									alignItems: 'center',
									width: '100%'
								}}
							>
								<Tooltip title="Copy CDR to clipboard" placement="top" arrow>
									<CopyAllIcon
										fontSize="small"
										color="primary"
										aria-label="Copy CDR ID and show profile"
										onClick={copyToClipboard}
										style={{ cursor: 'pointer' }}
									/>
								</Tooltip>
							</Box>
						</Col>
					</Row>
					<Row>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Calling Party</h6>
								</legend>
								<RtxTextInput
									label="Source"
									displayMode={false}
									value={cdrProfile.cp}
								/>
								<RtxTextInput
									label="LRN"
									displayMode={false}
									value={cdrProfile.cpLrn}
								/>
								<RtxTextInput
									label="Standardized"
									displayMode={false}
									value={cdrProfile.cpStd}
								/>
								<RtxRateCenterSelect
									displayMode={false}
									displayOnly={true}
									isDisabled={true}
									label="Rate Center"
									//cSpell:ignore irci
									initialOptionId={cdrProfile.irci}
									value={iRateCenter}
									onChange={setIRateCenter}
								/>
							</fieldset>
						</Col>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Dialed Number</h6>
								</legend>
								<RtxTextInput
									label="Source"
									displayMode={false}
									value={cdrProfile.dn}
								/>
								<RtxTextInput
									label="LRN"
									displayMode={false}
									value={cdrProfile.dnLrn}
								/>
								<RtxTextInput
									label="Standardized"
									displayMode={false}
									value={cdrProfile.dnStd}
								/>
								<RtxRateCenterSelect
									displayMode={true}
									displayOnly={true}
									label="Rate Center"
									//cSpell:ignore erci
									initialOptionId={cdrProfile.erci}
									value={eRateCenter}
									onChange={setERateCenter}
								/>
							</fieldset>
						</Col>
					</Row>
					<Row>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Ingress Signaling</h6>
								</legend>
								<RtxGatewaySelect
									displayMode={true}
									displayOnly={true}
									label="Local Gateway"
									initialOptionId={cdrProfile.iGatewayId}
									value={iGateway}
									onChange={setIGateway}
								/>
								<RtxSipConnectionSelect
									displayMode={true}
									displayOnly={true}
									label="Remote Gateway"
									initialOptionId={cdrProfile.iPeerId}
									value={iPeerId}
									onChange={setIPeerId}
									linkTo={
										iPeerId?.sipConnectionId !== undefined
											? ConnectionRouter.getProfileRoute(
													iPeerId.sipConnectionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Invite Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.iInviteTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss.SSS'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Answer Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.iAnswerTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss.SSS'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Release Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.iReleaseTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss.SSS'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={String(cdrProfile.iDuration)}
								/>
								<RtxTextInput
									label="Result"
									displayMode={false}
									value={
										cdrProfile && getCallProcessingResult(cdrProfile.iResult)
									}
								/>
								<RtxTextInput
									label="Call Id"
									displayMode={false}
									value={cdrProfile.iCallId}
									linkTo={
										!tabs.IngressSipTrace.isHidden
											? CdrSearchReportRouter.getProfileRoute(
													{
														cdrId: cdrProfile.cdrId,
														invite: cdrProfile.invite
													} as unknown as SipCdrSearchIndexResponse,
													tabs.IngressSipTrace.header
												)
											: undefined
									}
								/>
							</fieldset>
						</Col>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Egress Signaling</h6>
								</legend>
								<RtxGatewaySelect
									displayMode={true}
									displayOnly={true}
									label="Local Gateway"
									initialOptionId={cdrProfile.eGatewayId}
									value={eGateway}
									onChange={setEGateway}
								/>
								<RtxSipConnectionSelect
									displayMode={true}
									displayOnly={true}
									label="Remote Gateway"
									initialOptionId={cdrProfile.ePeerId}
									value={ePeerId}
									onChange={setEPeerId}
									linkTo={
										ePeerId?.sipConnectionId !== undefined
											? ConnectionRouter.getProfileRoute(
													ePeerId.sipConnectionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Invite Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.eInviteTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Answer Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.eAnswerTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Release Timestamp"
									displayMode={false}
									value={timestampToReadable(
										cdrProfile.eReleaseTs,
										'UTC',
										true,
										'YYYY-MM-DD HH:mm:ss'
									)}
									sx={{ backgroundColor: '#e3f2fd' }}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={
										cdrProfile.eDuration ? String(cdrProfile.eDuration) : ''
									}
								/>
								<RtxTextInput
									label="Result"
									displayMode={false}
									value={
										cdrProfile && getCallProcessingResult(cdrProfile.eResult)
									}
								/>
								<RtxTextInput
									label="Call Id"
									displayMode={false}
									value={cdrProfile.eCallId}
									linkTo={
										!tabs.EgressSipTrace.isHidden && cdrProfile.eCallId
											? CdrSearchReportRouter.getProfileRoute(
													{
														cdrId: cdrProfile.cdrId,
														invite: cdrProfile.invite
													} as unknown as SipCdrSearchIndexResponse,
													tabs.EgressSipTrace.header
												)
											: undefined
									}
								/>
							</fieldset>
						</Col>
					</Row>
				</RtUiForm>

				{cdrProfile.routeAttempts && cdrProfile.routeAttempts.length > 0 && (
					<Row className="m-3">
						<Col>
							<h5>Vendor Route Attempts</h5>
							<Table striped bordered>
								<th>Attempt</th>
								<th>Timestamp</th>
								<th>Subscription</th>
								<th>Vendor GW</th>
								<th>Tech Prefix</th>
								<th>Vendor PDD (ms)</th>
								<th>Dialed Number</th>
								<th>Release</th>
								<th>CallId</th>
								{cdrProfile.routeAttempts.map((routeAttempt, i) => {
									const linkTo = CdrSearchReportRouter.getProfileRoute(
										{
											cdrId: routeAttempt.aEgrCallId,
											invite: routeAttempt.aEgrInvTs
										} as unknown as SipCdrSearchIndexResponse,
										tabs.IngressSipTrace.header
									);
									return (
										<tr key={`route_attempt_${i}`}>
											<td>{routeAttempt.aEgrRouteDepth}</td>
											<td>
												{routeAttempt.aEgrInvTs
													? timestampToReadable(
															routeAttempt.aEgrInvTs,
															'UTC',
															true,
															'YYYY-MM-DD HH:mm:ss'
														)
													: ''}
											</td>
											<td>{routeAttempt.aEgrCostSubsId}</td>
											<td>{routeAttempt.egrSummary}</td>
											<td>{routeAttempt.egrTechPrefix}</td>
											<td>{routeAttempt.aPostDialDelay}</td>
											<td>{routeAttempt.aEgrNumber}</td>
											<td>
												{!releaseCodes.loading &&
													getReleaseCodeReadable(routeAttempt.aEgrReleaseId)}
											</td>
											<td>
												{routeAttempt.aEgrCallId}
												{/* {linkTo && (
													<Box>
														<Link href={linkTo} target="_blank" rel="noopener">
															<LaunchIcon fontSize="small" />
														</Link>
													</Box>
												)} */}
											</td>
										</tr>
									);
								})}
							</Table>
						</Col>
					</Row>
				)}

				<RtUiForm hideButtons createMode={false} displayMode={true} isInline>
					<Row>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Ingress Customer Rating</h6>
								</legend>
								<RtxAccountSelect
									displayMode={true}
									value={iVendorAccount}
									onChange={setIVendorAccount}
									initialOptionId={cdrProfile.iRevenueCarrierId}
									displayOnly={true}
									linkTo={
										iVendorAccount?.accountId !== undefined
											? AccountRouter.getProfileRoute(iVendorAccount.accountId)
											: undefined
									}
								/>
								<RtxSubscriptionSelect
									showAccountInfo={false}
									displayMode={true}
									value={iVendorSubscription}
									onChange={setIVendorSubscription}
									initialOptionId={cdrProfile.iRevenueSubscriptionId}
									displayOnly={true}
									linkTo={
										iVendorSubscription?.subscriptionId !== undefined
											? SubscriptionRouter.getProfileRoute(
													iVendorSubscription.subscriptionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Jurisdiction"
									displayMode={false}
									value={jurisdictionLabel(cdrProfile.iRevenueJurisdictionId)}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={String(cdrProfile.iRevenueDuration)}
								/>
								<RtxTextInput
									label="Rate Key"
									displayMode={false}
									value={cdrProfile.iRevenueRateKey}
								/>
								<RtxTextInput
									label="Rate"
									displayMode={false}
									value={cdrProfile.iRevenueRate.toFixed(8)}
								/>
								<RtxTextInput
									label="Charges"
									displayMode={false}
									value={String(cdrProfile.iRevenueRateCharge)}
								/>
							</fieldset>
						</Col>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Ingress Vendor Rating</h6>
								</legend>
								<RtxAccountSelect
									displayMode={true}
									value={iCustAccount}
									onChange={setICustAccount}
									initialOptionId={cdrProfile.iCostCarrierId}
									displayOnly={true}
									linkTo={
										iCustAccount?.accountId !== undefined
											? AccountRouter.getProfileRoute(iCustAccount.accountId)
											: undefined
									}
								/>
								<RtxSubscriptionSelect
									showAccountInfo={false}
									displayMode={true}
									value={iCustSubscription}
									onChange={setICustSubscription}
									initialOptionId={cdrProfile.iCostSubscriptionId}
									displayOnly={true}
									linkTo={
										iCustSubscription?.subscriptionId !== undefined
											? SubscriptionRouter.getProfileRoute(
													iCustSubscription.subscriptionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Jurisdiction"
									displayMode={false}
									value={jurisdictionLabel(cdrProfile.iCostJurisdictionId)}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={String(cdrProfile.iCostDuration)}
								/>
								<RtxTextInput
									label="Rate Key"
									displayMode={false}
									value={cdrProfile.iCostRateKey}
								/>
								<RtxTextInput
									label="Rate"
									displayMode={false}
									value={cdrProfile.iCostRate.toFixed(8)}
								/>
								<RtxTextInput
									label="Charges"
									displayMode={false}
									value={String(cdrProfile.iCostRateCharge)}
								/>
							</fieldset>
						</Col>
					</Row>
					<Row>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Egress Customer Rating</h6>
								</legend>
								<RtxAccountSelect
									displayMode={true}
									value={eVendorAccount}
									onChange={setEVendorAccount}
									initialOptionId={cdrProfile.eRevenueCarrierId}
									displayOnly={true}
									linkTo={
										eVendorAccount?.accountId !== undefined
											? AccountRouter.getProfileRoute(eVendorAccount.accountId)
											: undefined
									}
								/>
								<RtxSubscriptionSelect
									showAccountInfo={false}
									displayMode={true}
									value={eVendorSubscription}
									onChange={setEVendorSubscription}
									initialOptionId={cdrProfile.eRevenueSubscriptionId}
									displayOnly={true}
									linkTo={
										eVendorSubscription?.subscriptionId !== undefined
											? SubscriptionRouter.getProfileRoute(
													eVendorSubscription.subscriptionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Jurisdiction"
									displayMode={false}
									value={jurisdictionLabel(cdrProfile.eRevenueJurisdictionId)}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={String(cdrProfile.eRevenueDuration)}
								/>
								<RtxTextInput
									label="Rate Key"
									displayMode={false}
									value={cdrProfile.eRevenueRateKey}
								/>
								<RtxTextInput
									label="Rate"
									displayMode={false}
									value={cdrProfile.eRevenueRate.toFixed(8)}
								/>
								<RtxTextInput
									label="Charges"
									displayMode={false}
									value={cdrProfile.eRevenueRateCharge}
								/>
							</fieldset>
						</Col>
						<Col lg={6}>
							<fieldset className="card card-body mb-0">
								<legend className="card-header">
									<h6>Egress Vendor Rating</h6>
								</legend>
								<RtxAccountSelect
									displayMode={true}
									value={eCustAccount}
									onChange={setECustAccount}
									initialOptionId={cdrProfile.eCostCarrierId}
									displayOnly={true}
									linkTo={
										eCustAccount?.accountId !== undefined
											? AccountRouter.getProfileRoute(eCustAccount.accountId)
											: undefined
									}
								/>
								<RtxSubscriptionSelect
									showAccountInfo={false}
									displayMode={true}
									value={eCustSubscription}
									onChange={setECustSubscription}
									initialOptionId={cdrProfile.eCostSubscriptionId}
									displayOnly={true}
									linkTo={
										eCustSubscription?.subscriptionId !== undefined
											? SubscriptionRouter.getProfileRoute(
													eCustSubscription.subscriptionId
												)
											: undefined
									}
								/>
								<RtxTextInput
									label="Jurisdiction"
									displayMode={false}
									value={jurisdictionLabel(cdrProfile.eCostJurisdictionId)}
								/>
								<RtxTextInput
									label="Duration (secs)"
									displayMode={false}
									value={String(cdrProfile.eCostDuration)}
								/>
								<RtxTextInput
									label="Rate Key"
									displayMode={false}
									value={cdrProfile.eCostRateKey}
								/>
								<RtxTextInput
									label="Rate"
									displayMode={false}
									value={cdrProfile.eCostRate.toFixed(8)}
								/>
								<RtxTextInput
									label="Charges"
									displayMode={false}
									value={String(cdrProfile.eCostRateCharge)}
								/>
							</fieldset>
						</Col>
					</Row>
					{UserActions.has(Permissions.RtxAdministrator) && (
						<Row>
							<Col lg={6}>
								<fieldset className="card card-body mb-0">
									<legend className="card-header">
										<h6>Network Detail</h6>
									</legend>
									<RtxTextInput
										label="Media Id"
										displayMode={false}
										value={String(cdrProfile.mediaId)}
									/>
									<RtxTextInput
										label="Server Id"
										displayMode={false}
										value={String(cdrProfile.serverId)}
										linkTo={ServerRouter.getProfileRoute(cdrProfile.serverId)}
									/>
									<RtxTextInput
										label="Server Name"
										displayMode={false}
										value={
											cdrProfile.serverName ? String(cdrProfile.serverName) : ''
										}
									/>
								</fieldset>
							</Col>
						</Row>
					)}
				</RtUiForm>
			</TabbedLayoutTab>
			{sipTraceResource && (
				<>
					<TabbedLayoutTab {...tabs.IngressSipTrace}>
						<Row>
							<Col lg={12} xl={8}>
								<Card>
									<Card.Body>
										<SipTraceView
											callId={cdrProfile.iCallId}
											resource={sipTraceResource}
										/>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</TabbedLayoutTab>
					<TabbedLayoutTab {...tabs.EgressSipTrace}>
						<Row>
							<Col lg={12} xl={8}>
								<Card>
									<Card.Body>
										<SipTraceView
											callId={cdrProfile.eCallId}
											resource={sipTraceResource}
										/>
									</Card.Body>
								</Card>
							</Col>
						</Row>
					</TabbedLayoutTab>
				</>
			)}
		</TabbedLayout>
	);
};

CdrSearchReportRouter.setProfileRtUiFunctionalComponent(
	CdrSearchReportProfileContainer
);
